import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink, MessageCircle } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  const blueTeamRoadmap = [
    {
      phase: 'Foundation',
      items: [
        'Understanding Cybersecurity Basics',
        'Network Fundamentals (TCP/IP, OSI Model)',
        'Operating Systems (Windows, Linux)',
        'Security Principles (CIA Triad)',
      ],
    },
    {
      phase: 'Core Skills',
      items: [
        'Security Monitoring & SIEM',
        'Incident Response Fundamentals',
        'Threat Intelligence',
        'Log Analysis',
      ],
    },
    {
      phase: 'Advanced',
      items: [
        'Malware Analysis',
        'Digital Forensics',
        'Threat Hunting',
        'Security Architecture',
      ],
    },
    {
      phase: 'Specialization',
      items: [
        'Cloud Security',
        'Application Security',
        'Network Defense',
        'Identity & Access Management',
      ],
    },
  ];

  const careerPaths = [
    {
      id: 'soc-analyst',
      title: 'SOC Analyst',
      description: 'Monitor, detect, and respond to security incidents',
      roadmapUrl: 'https://securityblue.team/why-btl1/',
      icon: '/soc-analyst-icon.svg',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      id: 'incident-responder',
      title: 'Incident Responder',
      description: 'Handle and mitigate security breaches and attacks',
      roadmapUrl: 'https://www.sans.org/cyber-security-skills-roadmap/',
      icon: '/incident-responder-icon.svg',
      color: 'from-purple-500 to-pink-500',
    },
    {
      id: 'threat-hunter',
      title: 'Threat Hunter',
      description: 'Proactively search for threats in the environment',
      roadmapUrl: 'https://attack.mitre.org/',
      icon: '/threat-hunter-icon.svg',
      color: 'from-green-500 to-emerald-500',
    },
    {
      id: 'security-engineer',
      title: 'Security Engineer',
      description: 'Design and implement security solutions',
      roadmapUrl: 'https://roadmap.sh/cyber-security',
      icon: '/security-engineer-icon.svg',
      color: 'from-orange-500 to-red-500',
    },
  ];

  const contributors = [
    {
      name: 'Subhash Popuri',
      role: 'Senior Security Researcher',
      company: 'Microsoft',
      linkedin: '#',
      image: 'https://ui-avatars.com/api/?name=Subhash+Popuri&size=150&background=1a1f3a&color=00ff9f&bold=true',
    },
    {
      name: 'Chaitanya H',
      role: 'Founder & Threat Hunter',
      company: '[Confidential]',
      linkedin: '#',
      image: 'https://ui-avatars.com/api/?name=Chaitanya+H&size=150&background=1a1f3a&color=00d4ff&bold=true',
    },
    {
      name: 'Ankit Bhadri',
      role: 'Senior Cyber Security Specialist',
      company: 'Ankura',
      linkedin: '#',
      image: 'https://ui-avatars.com/api/?name=Ankit+Bhadri&size=150&background=1a1f3a&color=00ff9f&bold=true',
    },
    {
      name: 'Ashish',
      role: 'Consultant',
      company: 'Worldline',
      linkedin: '#',
      image: 'https://ui-avatars.com/api/?name=Ashish&size=150&background=1a1f3a&color=00d4ff&bold=true',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        >
          <source src="/background-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-primary via-secondary to-primary opacity-50"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-blue/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-8">
            <img 
              src="/logo-final.png" 
              alt="Team Cerberus Logo" 
              className="h-auto max-h-48 md:max-h-64 w-auto object-contain"
            />
          </div>
          
          <div className="mb-6">
            <p className="text-accent text-3xl md:text-5xl lg:text-6xl font-extrabold font-mono mb-2 tracking-wider">
              TEAM CERBERUS
            </p>
            <p className="text-accent text-xl md:text-3xl font-bold font-mono">
              Presents
            </p>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-8 font-mono leading-tight">
            <span className="text-white">Blue Team Village</span>
            <br />
            <span className="text-accent glitch">Vizag Edition</span>
          </h1>
          
          <p className="text-gray-200 text-xl md:text-2xl font-semibold mb-12 max-w-4xl mx-auto leading-relaxed">
            Your comprehensive platform for mastering defensive cybersecurity. 
            Learn, practice, and excel in protecting digital assets.
          </p>
          
          <button
            onClick={() => navigate('/resources')}
            className="group bg-accent hover:bg-accent-blue text-primary font-bold py-5 px-10 rounded-lg font-mono text-xl md:text-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-accent/50 inline-flex items-center space-x-3"
          >
            <span>Start Your Journey</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>

          <div className="mt-16 flex justify-center">
            <div className="text-accent animate-bounce">
              <svg className="w-8 h-8" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-center mb-6 font-mono">
            <span className="text-accent">&gt;</span> Your Blue Team Roadmap
          </h2>
          <p className="text-gray-300 text-xl md:text-2xl font-semibold text-center mb-20 max-w-3xl mx-auto">
            A structured path to becoming a skilled defensive security professional
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {blueTeamRoadmap.map((phase, index) => (
              <div
                key={index}
                className="bg-primary border-2 border-accent/20 rounded-lg p-8 hover:border-accent transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-extrabold text-accent font-mono">
                    {String(index + 1).padStart(2, '0')}. {phase.phase}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {phase.items.map((item, i) => (
                    <li key={i} className="text-gray-200 text-base font-semibold flex items-start leading-relaxed">
                      <span className="text-accent mr-3 text-xl">▹</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Paths Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-center mb-6 font-mono">
            <span className="text-accent">&gt;</span> Career Paths in Blue Team
          </h2>
          <p className="text-gray-300 text-xl md:text-2xl font-semibold text-center mb-20 max-w-3xl mx-auto">
            Choose your specialization and explore detailed roadmaps
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {careerPaths.map((path) => (
              <div
                key={path.id}
                className="group bg-secondary border-2 border-accent/20 rounded-lg p-10 hover:border-accent transition-all duration-300 cursor-pointer transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-accent/20"
                onClick={() => window.open(path.roadmapUrl, '_blank')}
              >
                <div className="flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                  <img 
                    src={path.icon} 
                    alt={`${path.title} Icon`} 
                    className="w-20 h-20 object-contain"
                  />
                </div>
                
                <h3 className="text-3xl font-extrabold text-white mb-4 font-mono group-hover:text-accent transition-colors">
                  {path.title}
                </h3>
                
                <p className="text-gray-300 text-lg font-semibold mb-8 leading-relaxed">
                  {path.description}
                </p>
                
                <div className="flex items-center text-accent group-hover:text-accent-blue transition-colors font-mono font-bold text-lg">
                  <span>View Roadmap</span>
                  <ExternalLink className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-center mb-6 font-mono">
            <span className="text-accent">&gt;</span> About Us
          </h2>
          <p className="text-gray-300 text-xl md:text-2xl font-semibold text-center mb-20 max-w-3xl mx-auto">
            Meet the team behind Blue Team Village
          </p>

          {/* Contributors */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-20">
            {contributors.map((contributor, index) => (
              <div
                key={index}
                className="bg-primary border-2 border-accent/20 rounded-lg p-10 text-center hover:border-accent transition-all duration-300 transform hover:-translate-y-2"
              >
                <img
                  src={contributor.image}
                  alt={contributor.name}
                  className="w-40 h-40 rounded-full mx-auto mb-8 border-4 border-accent/30 hover:border-accent transition-all"
                />
                <h3 className="text-2xl font-extrabold text-white mb-3 font-mono">
                  {contributor.name}
                </h3>
                <p className="text-accent text-lg font-bold mb-2">{contributor.role}</p>
                <p className="text-gray-400 text-sm font-semibold mb-6">{contributor.company}</p>
              </div>
            ))}
          </div>

          {/* Team Cerberus Info */}
          <div className="bg-primary border-2 border-accent/20 rounded-lg p-10 md:p-16 max-w-5xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-extrabold text-accent mb-8 font-mono text-center">
              About Team Cerberus
            </h3>
            <p className="text-gray-200 text-xl md:text-2xl font-semibold leading-relaxed mb-10 text-center">
              Team Cerberus is a community of passionate cybersecurity professionals dedicated to 
              empowering the next generation of defenders. We believe in knowledge sharing, hands-on 
              learning, and building a strong security community in Vizag and beyond.
            </p>
            <div className="flex justify-center">
              <a
                href="https://discord.gg/EYmWz5Cu"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-accent hover:bg-accent-blue text-primary font-extrabold py-5 px-10 rounded-lg font-mono text-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center space-x-4"
              >
                <MessageCircle className="w-7 h-7" />
                <span>Join Our Discord</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
